import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getJournalEntries, acceptJournalEntry, postJournalEntry, deleteJournalEntry, reverseJournalEntry, clearError, clearSuccess } from '../../../../redux/Slices/authSlice';
import { getCostCenters } from '../../../../redux/Slices/CostCenterSlice';
import { FaPlus, FaEye, FaEdit, FaTrash, FaCheck, FaPaperPlane, FaUndo, FaPrint, FaFileExcel, FaFilter, FaPaperclip, FaFileAlt, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import {
  generatePdfFromElementIds,
  generateSingleElementPdf,
  waitForRender,
  datedFileName,
} from '../../../../shared/pdf/pdfEngine';
import * as XLSX from 'xlsx';
import PdfPreviewModal from '../Reports/components/PdfPreviewModal';
import { useTranslation } from 'react-i18next';
import { calculatePdfPagination, convertArabicNumbersToEnglish } from '../../../../shared/utils.js/utils';
import axios from 'axios';
import { BASE_URL, getToken, getCurrentLanguage } from '../../../../shared/utils.js/utils';

const LOCKED_STATUSES = ['posted', 'closed'];
const SORT_FIELDS = { DATE: 'entry_date', REFERENCE: 'reference', DESCRIPTION: 'description', STATUS: 'status', AMOUNT: 'amount', CREATED_BY: 'creator' };

const JournalEntries = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const { journalEntriesPagination, isLoading, error, success } = useSelector((s) => s.auth);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [allJournalEntries, setAllJournalEntries] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [pdfChunks, setPdfChunks] = useState([]);
  const [allJournalEntriesForPDF, setAllJournalEntriesForPDF] = useState([]);
  const [attachmentsModal, setAttachmentsModal] = useState({ open: false, attachments: [], entryId: null });
  const lastErrorRef = useRef({ message: null, time: 0 });
  const lastSuccessRef = useRef({ message: null, time: 0 });
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isSinglePreviewOpen, setIsSinglePreviewOpen] = useState(false);
  const [singleEntryForPreview, setSingleEntryForPreview] = useState(null);
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const dir = isRTL ? 'rtl' : 'ltr';
  const textAlign = isRTL ? 'right' : 'left';
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [accountId, setAccountId] = useState('');
  const [costCenterId, setCostCenterId] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [showFilters, setShowFilters] = useState(true);
  // true once the first fetch settles, so an empty list stops showing the loading screen
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // Applied filter states (used for fetching)
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [appliedStatusFilter, setAppliedStatusFilter] = useState('');
  const [appliedStartDate, setAppliedStartDate] = useState('');
  const [appliedEndDate, setAppliedEndDate] = useState('');
  const [appliedAccountId, setAppliedAccountId] = useState('');
  const [appliedCostCenterId, setAppliedCostCenterId] = useState('');
  const [appliedMinAmount, setAppliedMinAmount] = useState('');
  const [appliedMaxAmount, setAppliedMaxAmount] = useState('');

  // Account dropdown states
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const [accountSearchTerm, setAccountSearchTerm] = useState('');
  const [accountPage, setAccountPage] = useState(1);
  const [accountOptions, setAccountOptions] = useState([]);
  const [accountPagination, setAccountPagination] = useState(null);
  const accountSearchInputRef = useRef(null);
  const accountSearchTimeoutRef = useRef(null);

  // Cost center dropdown states
  const [costCenterDropdownOpen, setCostCenterDropdownOpen] = useState(false);
  const [costCenterSearchTerm, setCostCenterSearchTerm] = useState('');
  const [costCenterPage, setCostCenterPage] = useState(1);
  const [costCenterOptions, setCostCenterOptions] = useState([]);
  const [costCenterPagination, setCostCenterPagination] = useState(null);
  const costCenterSearchInputRef = useRef(null);
  const costCenterSearchTimeoutRef = useRef(null);

  // Labels for selected account/cost center (used in PDF applied filters section)
  const [selectedAccountLabel, setSelectedAccountLabel] = useState('');
  const [selectedCostCenterLabel, setSelectedCostCenterLabel] = useState('');

  // Table headers with translations - recalculated when language changes
  const tableHeaders = React.useMemo(() => [
    { label: t('journalEntries.table.serialNumber'), width: '60px' },
    { label: t('journalEntries.table.date'), width: '140px' },
    { label: t('journalEntries.table.reference'), width: '180px' },
    { label: t('journalEntries.table.description'), width: '250px' },
    { label: t('journalEntries.table.status'), width: '140px' },
    { label: t('journalEntries.table.amount'), width: '160px' },
    { label: t('journalEntries.table.createdBy'), width: '160px' },
    { label: t('journalEntries.table.actions'), width: '240px' }
  ], [t]);
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <FaSort style={{ marginLeft: '4px', color: '#ccc', verticalAlign: 'middle' }} />;
    return sortOrder === 'asc'
      ? <FaSortUp style={{ marginLeft: '4px', color: '#ffd700', verticalAlign: 'middle' }} />
      : <FaSortDown style={{ marginLeft: '4px', color: '#ffd700', verticalAlign: 'middle' }} />;
  };

  const sortEntries = (entries, field, order) => {
    if (!field || !entries) return entries;
    return [...entries].sort((a, b) => {
      let aVal, bVal;
      if (field === 'entry_date') {
        aVal = a.entry_date || a.date || '';
        bVal = b.entry_date || b.date || '';
      } else if (field === 'amount') {
        const aDebit = parseFloat(a.total_debit || 0);
        const aCredit = parseFloat(a.total_credit || 0);
        aVal = aDebit !== 0 ? aDebit : aCredit;
        const bDebit = parseFloat(b.total_debit || 0);
        const bCredit = parseFloat(b.total_credit || 0);
        bVal = bDebit !== 0 ? bDebit : bCredit;
      } else if (field === 'creator') {
        aVal = a.creator || a.created_by?.name || '';
        bVal = b.creator || b.created_by?.name || '';
      } else {
        aVal = a[field] ?? '';
        bVal = b[field] ?? '';
      }
      if (typeof aVal === 'boolean') aVal = aVal ? 1 : 0;
      if (typeof bVal === 'boolean') bVal = bVal ? 1 : 0;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return order === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return order === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  };

  const publicUrl = process.env.PUBLIC_URL || '';
  const headerLogo = `${publicUrl}/ACE.png`;

  useEffect(() => {
    setTitle('sidenav.journalEntries');
  }, [setTitle]);

  // Fetch journal entries with filters
  const fetchJournalEntries = useCallback(async () => {
    try {
      const params = {
        page: currentPage,
        per_page: perPage
      };

      // Add filters
      if (appliedSearchTerm) params.search = appliedSearchTerm;
      if (appliedStatusFilter) params.status = appliedStatusFilter;
      if (appliedStartDate) params.start_date = appliedStartDate;
      if (appliedEndDate) params.end_date = appliedEndDate;
      if (appliedAccountId) params.account_id = appliedAccountId;
      if (appliedCostCenterId) params.cost_center_id = appliedCostCenterId;
      if (appliedMinAmount) params.min_amount = appliedMinAmount;
      if (appliedMaxAmount) params.max_amount = appliedMaxAmount;

      const result = await dispatch(getJournalEntries(params));
      if (getJournalEntries.fulfilled && result.type === getJournalEntries.fulfilled.type) {
        const response = result.payload;
        const responseData = response?.data || response;
        const paginationData = response?.meta || responseData?.meta || responseData?.pagination || journalEntriesPagination || null;
        let entriesList = [];

        // Extract entries from response
        if (Array.isArray(responseData?.data)) {
          entriesList = responseData.data;
        } else if (Array.isArray(responseData)) {
          entriesList = responseData;
        } else if (Array.isArray(response)) {
          entriesList = response;
        }

        setAllJournalEntries(entriesList);
        setPagination(paginationData);
      }
    } catch (err) {
    } finally {
      setHasLoadedOnce(true);
    }
  }, [dispatch, currentPage, perPage, appliedSearchTerm, appliedStatusFilter, appliedStartDate, appliedEndDate, appliedAccountId, appliedCostCenterId, appliedMinAmount, appliedMaxAmount]);

  // Fetch all journal entries with per_page = total for PDF/Excel export
  const fetchAllJournalEntriesForExport = async () => {
    try {
      // First fetch to get total count (with applied filters)
      const params = {
        page: 1,
        per_page: 10
      };
      if (appliedSearchTerm) params.search = appliedSearchTerm;
      if (appliedStatusFilter) params.status = appliedStatusFilter;
      if (appliedStartDate) params.start_date = appliedStartDate;
      if (appliedEndDate) params.end_date = appliedEndDate;
      if (appliedAccountId) params.account_id = appliedAccountId;
      if (appliedCostCenterId) params.cost_center_id = appliedCostCenterId;
      if (appliedMinAmount) params.min_amount = appliedMinAmount;
      if (appliedMaxAmount) params.max_amount = appliedMaxAmount;

      const result = await dispatch(getJournalEntries(params));
      if (getJournalEntries.fulfilled && result.type === getJournalEntries.fulfilled.type) {
        const response = result.payload;
        const responseData = response?.data || response;
        const paginationData = response?.meta || responseData?.meta || responseData?.pagination || journalEntriesPagination || null;
        const total = paginationData?.total || response?.meta?.total || 0;

        if (total > 0) {
          // Fetch all entries with per_page = total (with applied filters)
          const allParams = {
            page: 1,
            per_page: total
          };
          if (appliedSearchTerm) allParams.search = appliedSearchTerm;
          if (appliedStatusFilter) allParams.status = appliedStatusFilter;
          if (appliedStartDate) allParams.start_date = appliedStartDate;
          if (appliedEndDate) allParams.end_date = appliedEndDate;
          if (appliedAccountId) allParams.account_id = appliedAccountId;
          if (appliedCostCenterId) allParams.cost_center_id = appliedCostCenterId;
          if (appliedMinAmount) allParams.min_amount = appliedMinAmount;
          if (appliedMaxAmount) allParams.max_amount = appliedMaxAmount;

          const allResult = await dispatch(getJournalEntries(allParams));
          if (getJournalEntries.fulfilled && allResult.type === getJournalEntries.fulfilled.type) {
            const allResponse = allResult.payload;
            const allResponseData = allResponse?.data || allResponse;
            let entriesList = [];

            if (Array.isArray(allResponseData?.data)) {
              entriesList = allResponseData.data;
            } else if (Array.isArray(allResponseData)) {
              entriesList = allResponseData;
            } else if (Array.isArray(allResponse)) {
              entriesList = allResponse;
            }

            return entriesList;
          }
        }
      }
      return [];
    } catch (err) {
      return [];
    }
  };

  // Fetch data for initial load and when pagination or applied filters change
  useEffect(() => {
    fetchJournalEntries();
  }, [fetchJournalEntries]);

  // Fetch accounts with search and pagination
  const fetchAccounts = useCallback(async (page = accountPage, search = accountSearchTerm) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) return;

      const queryParams = new URLSearchParams();
      if (page) queryParams.append('page', page);
      if (search) queryParams.append('search', search);
      queryParams.append('per_page', '10');

      const response = await axios.get(`${BASE_URL}/accounts/posting?${queryParams.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept-Language': currentLang,
        },
      });

      // Handle different response structures
      let data = [];
      let meta = null;

      if (Array.isArray(response?.data?.data)) {
        data = response.data.data;
        meta = response.data?.meta || null;
        // Check if pagination info is directly in data object
        if (!meta && (response.data.current_page !== undefined || response.data.last_page !== undefined)) {
          meta = {
            current_page: response.data.current_page || 1,
            last_page: response.data.last_page || 1,
            per_page: response.data.per_page || 10,
            total: response.data.total || data.length,
          };
        }
      } else if (Array.isArray(response?.data)) {
        data = response.data;
        meta = response?.meta || null;
      } else if (Array.isArray(response)) {
        data = response;
        meta = null;
      }

      setAccountOptions(Array.isArray(data) ? data : []);
      setAccountPagination(meta);
    } catch (error) {
      setAccountOptions([]);
      setAccountPagination(null);
    }
  }, [accountPage, accountSearchTerm]);

  // Fetch cost centers with search and pagination
  const fetchCostCenters = useCallback(async (page = costCenterPage, search = costCenterSearchTerm) => {
    try {
      const params = { page, per_page: 10 };
      if (search) params.search = search;

      const result = await dispatch(getCostCenters(params));
      if (getCostCenters.fulfilled.match(result)) {
        const response = result.payload;
        // Handle different response structures
        let data = [];
        let meta = null;

        if (Array.isArray(response?.data?.data)) {
          data = response.data.data;
          meta = response.data?.meta || null;
          // Check if pagination info is directly in data object
          if (!meta && (response.data.current_page !== undefined || response.data.last_page !== undefined)) {
            meta = {
              current_page: response.data.current_page || 1,
              last_page: response.data.last_page || 1,
              per_page: response.data.per_page || 10,
              total: response.data.total || data.length,
            };
          }
        } else if (Array.isArray(response?.data)) {
          data = response.data;
          meta = response?.meta || null;
        } else if (Array.isArray(response)) {
          data = response;
          meta = null;
        }

        setCostCenterOptions(Array.isArray(data) ? data : []);
        setCostCenterPagination(meta);
      }
    } catch (error) {
      setCostCenterOptions([]);
      setCostCenterPagination(null);
    }
  }, [dispatch, costCenterPage, costCenterSearchTerm]);

  // Debounce account search
  useEffect(() => {
    if (accountSearchTimeoutRef.current) {
      clearTimeout(accountSearchTimeoutRef.current);
    }
    accountSearchTimeoutRef.current = setTimeout(() => {
      if (accountDropdownOpen) {
        setAccountPage(1);
        fetchAccounts(1, accountSearchTerm);
      }
    }, 300);
    return () => {
      if (accountSearchTimeoutRef.current) {
        clearTimeout(accountSearchTimeoutRef.current);
      }
    };
  }, [accountSearchTerm, accountDropdownOpen, fetchAccounts]);

  // Debounce cost center search
  useEffect(() => {
    if (costCenterSearchTimeoutRef.current) {
      clearTimeout(costCenterSearchTimeoutRef.current);
    }
    costCenterSearchTimeoutRef.current = setTimeout(() => {
      if (costCenterDropdownOpen) {
        // Only reset to page 1 if search term actually changed (not empty to empty)
        if (costCenterSearchTerm !== '') {
          setCostCenterPage(1);
          fetchCostCenters(1, costCenterSearchTerm);
        } else {
          // If search is cleared, fetch with current page
          fetchCostCenters(costCenterPage, '');
        }
      }
    }, 300);
    return () => {
      if (costCenterSearchTimeoutRef.current) {
        clearTimeout(costCenterSearchTimeoutRef.current);
      }
    };
  }, [costCenterSearchTerm, costCenterDropdownOpen, fetchCostCenters, costCenterPage]);

  // Fetch accounts when dropdown opens
  useEffect(() => {
    if (accountDropdownOpen) {
      fetchAccounts(accountPage, accountSearchTerm);
    }
  }, [accountDropdownOpen, accountPage, fetchAccounts]);

  // Fetch cost centers when dropdown opens or page changes
  useEffect(() => {
    if (costCenterDropdownOpen) {
      fetchCostCenters(costCenterPage, costCenterSearchTerm);
    }
  }, [costCenterDropdownOpen, costCenterPage, fetchCostCenters, costCenterSearchTerm]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (accountDropdownOpen && !e.target.closest('.account-dropdown-container')) {
        setAccountDropdownOpen(false);
      }
      if (costCenterDropdownOpen && !e.target.closest('.cost-center-dropdown-container')) {
        setCostCenterDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [accountDropdownOpen, costCenterDropdownOpen]);

  // Handle apply per page
  const handleApplyPerPage = async () => {
    // Reset to first page; fetching will be triggered by useEffect
    setCurrentPage(1);
  };

  useEffect(() => {
    if (success) {
      const now = Date.now();
      const last = lastSuccessRef.current;
      if (!last.message || last.message !== success || now - last.time > 2000) {
        toast.success(success, {
          rtl: isRTL,
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        lastSuccessRef.current = { message: success, time: now };
      }
      // Only refresh when adding new entry
      if (success.includes('إضافة') || success.includes('تم إضافة') || success.includes('Add') || success.includes('added')) {
        fetchJournalEntries();
      }
      // Don't refresh on delete - it updates optimistically
      dispatch(clearSuccess());
    }
  }, [success, dispatch, isRTL, fetchJournalEntries]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, {
          rtl: isRTL,
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [error, dispatch, isRTL]);

  const handleShowAttachments = (entry) => {
    if (!entry) return;
    const attachments = Array.isArray(entry.attachments) ? entry.attachments : [];

    if (!attachments.length) {
      toast.info(t('journalEntries.attachments.noAttachments', { defaultValue: 'لا توجد مرفقات لهذا القيد' }), { rtl: isRTL });
      return;
    }

    setAttachmentsModal({
      open: true,
      attachments,
      entryId: entry.id || null,
    });
  };

  const handleAccept = (id) => dispatch(acceptJournalEntry(id));
  const handlePost = (id) => dispatch(postJournalEntry(id));
  const handleReverse = (id) => dispatch(reverseJournalEntry(id));
  const handleDelete = (id) => {
    const target = (allJournalEntries || []).find((e) => e.id === id) || { id };
    if (target?.status && LOCKED_STATUSES.includes(target.status)) {
      toast.error(t('journalEntries.delete.cannotDelete'), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      return;
    }
    setEntryToDelete(target);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!entryToDelete) return;
    if (entryToDelete?.status && LOCKED_STATUSES.includes(entryToDelete.status)) {
      toast.error(t('journalEntries.delete.cannotDelete'), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      setShowDeleteModal(false);
      setEntryToDelete(null);
      return;
    }

    const idToDelete = entryToDelete.id;
    const entryToDeleteBackup = { ...entryToDelete };

    // Don't do optimistic delete - we can't know if entry is linked to another system
    // Only delete optimistically if we're sure it can be deleted
    // For now, we'll let the API decide and only update on success

    setShowDeleteModal(false);
    setEntryToDelete(null);

    try {
      const result = await dispatch(deleteJournalEntry(idToDelete));
      // Check if the action was rejected
      if (deleteJournalEntry.rejected.match(result)) {
        throw new Error(result.payload || 'فشل حذف قيد اليومية');
      }
      // Only remove from local state after successful deletion
      setAllJournalEntries(prev => prev.filter(entry => entry.id !== idToDelete));
      // Error will be shown by useEffect if needed
    } catch (error) {
      // Don't show error here - useEffect will handle it
      // Just make sure entry is still in the list
      setAllJournalEntries(prev => {
        const exists = prev.find(entry => entry.id === idToDelete);
        if (!exists && entryToDeleteBackup) {
          const newList = [...prev, entryToDeleteBackup];
          return newList.sort((a, b) => {
            if (a.id && b.id) {
              if (typeof a.id === 'number' && typeof b.id === 'number') {
                return a.id - b.id;
              }
              return String(a.id).localeCompare(String(b.id));
            }
            return 0;
          });
        }
        return prev;
      });
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setEntryToDelete(null);
  };

  // Use entries from API (already paginated with per_page from state)
  const filteredJournalEntries = allJournalEntries || [];
  const sortedJournalEntries = useMemo(() => sortEntries(filteredJournalEntries, sortField, sortOrder), [filteredJournalEntries, sortField, sortOrder]);
  const totalPages = pagination?.last_page || 1;
  const currentPageSafe = Math.max(1, Math.min(currentPage, totalPages));
  const paginatedJournalEntries = sortedJournalEntries;
  const startIndex = (currentPageSafe - 1) * perPage;
  // Active filters list to display in the PDF header
  const activeFiltersForPdf = React.useMemo(() => {
    const parts = [];
    if (appliedSearchTerm) parts.push(`${t('journalEntries.filters.search')}: ${appliedSearchTerm}`);
    if (appliedStatusFilter) parts.push(`${t('journalEntries.filters.status')}: ${t(`journalEntries.status.${appliedStatusFilter}`)}`);
    if (appliedStartDate) parts.push(`${t('journalEntries.filters.startDate')}: ${appliedStartDate}`);
    if (appliedEndDate) parts.push(`${t('journalEntries.filters.endDate')}: ${appliedEndDate}`);
    if (appliedAccountId) parts.push(`${t('journalEntries.filters.account')}: ${selectedAccountLabel || appliedAccountId}`);
    if (appliedCostCenterId) parts.push(`${t('journalEntries.filters.costCenter')}: ${selectedCostCenterLabel || appliedCostCenterId}`);
    if (appliedMinAmount) parts.push(`${t('journalEntries.filters.minAmount')}: ${appliedMinAmount}`);
    if (appliedMaxAmount) parts.push(`${t('journalEntries.filters.maxAmount')}: ${appliedMaxAmount}`);
    return parts.join(' | ');
  }, [
    appliedSearchTerm, appliedStatusFilter, appliedStartDate, appliedEndDate,
    appliedAccountId, appliedCostCenterId, appliedMinAmount, appliedMaxAmount,
    selectedAccountLabel, selectedCostCenterLabel, t
  ]);

  // Preload image helper
  const preloadImage = (src) =>
    new Promise((resolve) => {
      if (!src) return resolve();
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = src;
    });

  // Handle building dynamic PDF chunks with the shared PDF pagination engine.
  const buildDynamicPdfChunks = (entries) => {
    if (!Array.isArray(entries) || entries.length === 0) {
      setPdfChunks([]);
      return [];
    }

    const topHeader = document.getElementById('journal-entries-pdf-measure-top-header');
    const thead = document.getElementById('journal-entries-pdf-measure-thead');
    const footer = document.getElementById('journal-entries-pdf-measure-footer');
    const tbody = document.getElementById('journal-entries-pdf-measure-tbody');

    const chunks = calculatePdfPagination(entries, { topHeader, thead, footer, tbody });

    setPdfChunks(chunks);
    return chunks;
  };

  // Prepare full journal entries data and perform dynamic pagination measurement
  const prepareAllJournalEntriesPdfData = async () => {
    const allEntries = await fetchAllJournalEntriesForExport();
    if (!allEntries || allEntries.length === 0) {
      toast.error(t('journalEntries.noData'), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      return [];
    }

    const sortedAll = sortEntries(allEntries, sortField, sortOrder);
    setAllJournalEntriesForPDF(sortedAll);
    // Wait for the hidden measure elements to render in the DOM
    await new Promise((resolve) => setTimeout(resolve, 80));
    await new Promise((resolve) => requestAnimationFrame(resolve));
    await new Promise((resolve) => requestAnimationFrame(resolve));

    return buildDynamicPdfChunks(sortedAll);
  };

  // Handle preview all journal entries
  const handlePreviewAll = async () => {
    try {
      const chunks = await prepareAllJournalEntriesPdfData();
      if (!chunks || chunks.length === 0) return;
      setIsPreviewOpen(true);
    } catch (error) {
      toast.error(error.message || t('journalEntries.preview.error'), { rtl: isRTL });
    }
  };

  // Handle print all journal entries
  const handlePrintAll = async () => {
    try {
      setIsGeneratingPdf(true);

      let chunks = pdfChunks;
      if (!chunks || chunks.length === 0) {
        chunks = await prepareAllJournalEntriesPdfData();
      }

      if (!chunks || chunks.length === 0) {
        setIsGeneratingPdf(false);
        return;
      }

      // Wait for DOM to update
      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => requestAnimationFrame(resolve));

      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }

      // Preload images
      await Promise.all([
        preloadImage(`${publicUrl}/HeaderAce.png`),
        preloadImage(`${publicUrl}/FooterAce.png`),
        preloadImage(headerLogo)
      ]);

      await waitForRender(800);

      // Shared PDF Engine — capture + assembly (one A4 page per chunk)
      await generatePdfFromElementIds({
        elementIds: chunks.map((_, i) => `journal-entries-pdf-all-${i}`),
        fileName: datedFileName(t('journalEntries.excel.fileName')),
        isRTL,
      });
      toast.success(t('journalEntries.print.success'), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } catch (error) {
      toast.error(error.message || t('journalEntries.print.error'), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Handle export all journal entries to Excel
  const handleExportAllToExcel = async () => {
    try {
      const allEntries = await fetchAllJournalEntriesForExport();
      if (!allEntries || allEntries.length === 0) {
        toast.error(t('journalEntries.noData'), { rtl: isRTL });
        return;
      }

      const data = sortEntries(allEntries, sortField, sortOrder).map((entry, index) => ({
        [t('journalEntries.table.serialNumber')]: index + 1,
        [t('journalEntries.table.date')]: (() => {
          const dateValue = entry.entry_date || entry.date;
          if (!dateValue) return '—';
          try {
            const date = new Date(dateValue);
            return convertArabicNumbersToEnglish(date.toLocaleDateString('en-US', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit'
            }));
          } catch {
            return dateValue;
          }
        })(),
        [t('journalEntries.table.reference')]: entry.reference || '—',
        [t('journalEntries.table.description')]: entry.description || '—',
        [t('journalEntries.table.status')]: entry.status ? t(`journalEntries.status.${entry.status}`) : '—',
        [t('journalEntries.table.amount')]: (() => {
          const debit = parseFloat(entry.total_debit || 0);
          const credit = parseFloat(entry.total_credit || 0);
          const value = debit !== 0 ? debit : credit;
          return convertArabicNumbersToEnglish(value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
        })(),
        [t('journalEntries.table.createdBy')]: entry.creator || entry.created_by?.name || '—'
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, t('journalEntries.excel.sheetName'));
      const fileName = `${t('journalEntries.excel.fileName')}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success(t('journalEntries.export.success'), { rtl: isRTL });
    } catch (error) {
      toast.error(error.message || t('journalEntries.export.error'), { rtl: isRTL });
    }
  };

  // Handle preview single journal entry
  const handlePreviewSingle = (entry) => {
    setSingleEntryForPreview(entry);
    setIsSinglePreviewOpen(true);
  };

  // Handle print single journal entry
  const handlePrintSingle = async (entry) => {
    if (!entry) return;

    try {
      setIsGeneratingPdf(true);

      // Create a temporary container for the PDF template
      const tempContainer = document.createElement('div');
      tempContainer.id = `journal-entry-pdf-single-${entry.id}`;
      tempContainer.setAttribute('aria-hidden', 'true');
      tempContainer.style.cssText = 'position: absolute; left: -9999px; top: 0; width: 794px; min-height: 1123px; height: auto; background-color: #ffffff; color: #000000; font-family: "Times New Roman Bold", "Times New Roman", "Times", serif; font-weight: bold; padding: 40px; box-sizing: border-box; overflow: visible;';
      document.body.appendChild(tempContainer);

      // Create the PDF template HTML
      const dateValue = entry.entry_date || entry.date;
      const formattedDate = dateValue ? (() => {
        try {
          const date = new Date(dateValue);
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          });
        } catch {
          return dateValue;
        }
      })() : '—';

      const debit = parseFloat(entry.total_debit || 0);
      const credit = parseFloat(entry.total_credit || 0);
      const amount = debit !== 0 ? debit : credit;
      const formattedAmount = amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

      tempContainer.innerHTML = `
        <div style="border: 5px solid #000; padding: 3px; background-color: #ffffff; box-sizing: border-box; width: 100%; min-height: 1043px; height: auto; overflow: visible;">
          <div style="border: 2px solid #000; min-height: 1033px; height: 1033px; padding: 18px 18px 10px; position: relative; display: flex; flex-direction: column; font-family: 'Times New Roman Bold', 'Times New Roman', 'Times', serif; font-weight: bold; direction: ${isRTL ? 'rtl' : 'ltr'}; background-color: #ffffff; box-sizing: border-box; width: 100%; overflow: visible;">
            <div style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; background-image: url(${headerLogo}); background-size: 95%; background-position: center center; background-repeat: no-repeat; opacity: 0.08; z-index: 0; pointer-events: none;"></div>
            <div style="position: relative; z-index: 1; display: flex; flex-direction: column; height: 100%; min-height: 100%;">
              <img src="${publicUrl}/HeaderAce.png" alt="Header" style="width: 100%; height: auto; object-fit: contain; margin-bottom: 8px; border-bottom: 4px double #000; padding-bottom: 8px; flex-shrink: 0;" crossOrigin="anonymous" />
              <div style="flex: 1; display: flex; flex-direction: column; overflow: visible; min-height: 0; padding-bottom: 120px;">
                <div style="margin-top: 12px; text-align: center; margin-bottom: 10px;">
                  <h2 style="font-size: 20px; font-weight: 700; color: #000080; margin: 0; direction: ${dir};">${t('journalEntries.title')}</h2>
                  <p style="font-size: 11px; color: #555; margin: 6px 0 0 0;">${new Date().toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                </div>
                <div style="direction: ${isRTL ? 'rtl' : 'ltr'}; font-family: 'Times New Roman', 'Times', serif;">
                  <table style="width: 100%; border-collapse: collapse; font-size: 11px; border: 1px solid #000; font-family: 'Times New Roman', 'Times', serif; background-color: rgba(255, 255, 255, 0.7);">
                    <thead>
                      <tr style="background-color: rgba(238, 242, 247, 0.7);">
                        <th style="border: 1px solid #000; padding: 6px; text-align: center; font-weight: 700; background-color: rgba(238, 242, 247, 0.7);">${t('journalEntries.table.serialNumber')}</th>
                        <th style="border: 1px solid #000; padding: 6px; text-align: center; font-weight: 700; background-color: rgba(238, 242, 247, 0.7);">${t('journalEntries.table.date')}</th>
                        <th style="border: 1px solid #000; padding: 6px; text-align: center; font-weight: 700; background-color: rgba(238, 242, 247, 0.7);">${t('journalEntries.table.reference')}</th>
                        <th style="border: 1px solid #000; padding: 6px; text-align: center; font-weight: 700; background-color: rgba(238, 242, 247, 0.7);">${t('journalEntries.table.description')}</th>
                        <th style="border: 1px solid #000; padding: 6px; text-align: center; font-weight: 700; background-color: rgba(238, 242, 247, 0.7);">${t('journalEntries.table.status')}</th>
                        <th style="border: 1px solid #000; padding: 6px; text-align: center; font-weight: 700; background-color: rgba(238, 242, 247, 0.7);">${t('journalEntries.table.amount')}</th>
                        <th style="border: 1px solid #000; padding: 6px; text-align: center; font-weight: 700; background-color: rgba(238, 242, 247, 0.7);">${t('journalEntries.table.createdBy')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style="background-color: rgba(255, 255, 255, 0.7);">
                        <td style="border: 1px solid #000; padding: 6px; text-align: center; background-color: rgba(255, 255, 255, 0.7);">1</td>
                        <td style="border: 1px solid #000; padding: 6px; text-align: center; background-color: rgba(255, 255, 255, 0.7);">${formattedDate}</td>
                        <td style="border: 1px solid #000; padding: 6px; text-align: center; background-color: rgba(255, 255, 255, 0.7);">${entry.reference || '—'}</td>
                        <td style="border: 1px solid #000; padding: 6px; text-align: center; background-color: rgba(255, 255, 255, 0.7);">${entry.description || '—'}</td>
                        <td style="border: 1px solid #000; padding: 6px; text-align: center; background-color: rgba(255, 255, 255, 0.7);">${t(`journalEntries.status.${entry.status || 'draft'}`)}</td>
                        <td style="border: 1px solid #000; padding: 6px; text-align: center; background-color: rgba(255, 255, 255, 0.7);">${formattedAmount}</td>
                        <td style="border: 1px solid #000; padding: 6px; text-align: center; background-color: rgba(255, 255, 255, 0.7);">${entry.creator || entry.created_by?.name || '—'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div style="position: absolute; bottom: 10px; left: 18px; right: 18px; flex-shrink: 0;">
              <img src="${publicUrl}/FooterAce.png" alt="Footer" style="width: 100%; height: auto; object-fit: contain; border-top: 4px double #000; padding-top: 8px; flex-shrink: 0;" crossOrigin="anonymous" />
            </div>
          </div>
        </div>
      `;

      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => requestAnimationFrame(resolve));

      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }

      await Promise.all([
        preloadImage(`${publicUrl}/HeaderAce.png`),
        preloadImage(`${publicUrl}/FooterAce.png`),
        preloadImage(headerLogo)
      ]);

      await waitForRender(800);

      // Shared PDF Engine — capture + assembly
      await generateSingleElementPdf({
        elementId: tempContainer.id,
        fileName: datedFileName(`${t('journalEntries.excel.fileName')}_${entry.id}`),
        isRTL,
      });

      // Clean up
      document.body.removeChild(tempContainer);

      toast.success(t('journalEntries.print.success'), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } catch (error) {
      toast.error(error.message || t('journalEntries.print.error'), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Handle export single journal entry to Excel
  const handleExportSingleToExcel = (entry) => {
    if (!entry) return;

    try {
      const data = [{
        [t('journalEntries.table.serialNumber')]: 1,
        [t('journalEntries.table.date')]: (() => {
          const dateValue = entry.entry_date || entry.date;
          if (!dateValue) return '—';
          try {
            const date = new Date(dateValue);
            return date.toLocaleDateString('en-US', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit'
            });
          } catch {
            return dateValue;
          }
        })(),
        [t('journalEntries.table.reference')]: entry.reference || '—',
        [t('journalEntries.table.description')]: entry.description || '—',
        [t('journalEntries.table.status')]: entry.status ? t(`journalEntries.status.${entry.status}`) : '—',
        [t('journalEntries.table.amount')]: (() => {
          const debit = parseFloat(entry.total_debit || 0);
          const credit = parseFloat(entry.total_credit || 0);
          return debit !== 0 ? debit : credit;
        })(),
        [t('journalEntries.table.createdBy')]: entry.creator || entry.created_by?.name || '—'
      }];

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, t('journalEntries.excel.sheetName'));
      const fileName = `${t('journalEntries.excel.fileName')}_${entry.id}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success(t('journalEntries.export.success'), { rtl: isRTL });
    } catch (error) {
      toast.error(error.message || t('journalEntries.export.error'), { rtl: isRTL });
    }
  };

  if (isLoading && !hasLoadedOnce && (!allJournalEntries || allJournalEntries.length === 0)) {
    return (
      <div style={{ padding: '30px', backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', direction: dir }}>
        {t('common.loading')}
      </div>
    );
  }

  return (
    <React.Fragment>
      <div style={{ padding: '30px', backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white', direction: dir }}>
        <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <h1 style={{ fontSize: '20px', margin: 0, textAlign }}>{t('journalEntries.title')}</h1>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ color: 'white', fontSize: '14px', whiteSpace: 'nowrap', textAlign: isRTL ? 'right' : 'left' }}>
                {t('common.perPage', { defaultValue: 'Per Page' })}:
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <button
                  onClick={() => {
                    const newValue = Math.max(1, perPage - 1);
                    setPerPage(newValue);
                  }}
                  style={{
                    backgroundColor: '#1b3350',
                    color: 'white',
                    border: '1px solid #333',
                    padding: '8px 12px',
                    borderRadius: '6px 0 0 6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '36px'
                  }}
                >
                  −
                </button>
                <input
                  type="number"
                  min="1"
                  max={pagination?.total || 1000}
                  value={perPage}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 1;
                    const maxValue = pagination?.total || 1000;
                    setPerPage(Math.max(1, Math.min(maxValue, value)));
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleApplyPerPage();
                    }
                  }}
                  style={{
                    width: '80px',
                    padding: '8px 12px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderLeft: 'none',
                    borderRight: 'none',
                    borderRadius: '0',
                    color: 'white',
                    fontSize: '14px',
                    textAlign: 'center'
                  }}
                />
                <button
                  onClick={() => {
                    const maxValue = pagination?.total || 1000;
                    const newValue = Math.min(maxValue, perPage + 1);
                    setPerPage(newValue);
                  }}
                  style={{
                    backgroundColor: '#1b3350',
                    color: 'white',
                    border: '1px solid #333',
                    padding: '8px 12px',
                    borderRadius: '0 6px 6px 0',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '36px'
                  }}
                >
                  +
                </button>
              </div>
              <button
                onClick={handleApplyPerPage}
                style={{
                  backgroundColor: '#AC2000',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {t('common.apply', { defaultValue: 'تطبيق' })}
              </button>
            </div>
            <button
              onClick={handlePreviewAll}
              disabled={!pagination || pagination.total === 0}
              style={{
                backgroundColor: !pagination || pagination.total === 0 ? '#666' : '#444',
                color: 'white',
                border: 'none',
                padding: '10px 14px',
                borderRadius: '8px',
                cursor: !filteredJournalEntries || filteredJournalEntries.length === 0 ? 'not-allowed' : 'pointer',
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                flexDirection: isRTL ? 'row' : 'row-reverse'
              }}
            >
              <FaEye /> {t('journalEntries.preview.all')}
            </button>
            <button
              onClick={handlePrintAll}
              disabled={isGeneratingPdf || !pagination || pagination.total === 0}
              style={{
                backgroundColor: isGeneratingPdf || !pagination || pagination.total === 0 ? '#666' : '#1f5ff1',
                color: 'white',
                border: 'none',
                padding: '10px 14px',
                borderRadius: '8px',
                cursor: isGeneratingPdf || !filteredJournalEntries || filteredJournalEntries.length === 0 ? 'not-allowed' : 'pointer',
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                flexDirection: isRTL ? 'row' : 'row-reverse',
                opacity: isGeneratingPdf ? 0.6 : 1
              }}
            >
              <FaPrint /> {isGeneratingPdf ? t('journalEntries.print.generating') : t('journalEntries.print.all')}
            </button>
            <button
              onClick={handleExportAllToExcel}
              disabled={!pagination || pagination.total === 0}
              style={{
                backgroundColor: !pagination || pagination.total === 0 ? '#666' : '#27ae60',
                color: 'white',
                border: 'none',
                padding: '10px 14px',
                borderRadius: '8px',
                cursor: !filteredJournalEntries || filteredJournalEntries.length === 0 ? 'not-allowed' : 'pointer',
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                flexDirection: isRTL ? 'row' : 'row-reverse'
              }}
            >
              <FaFileExcel /> {t('journalEntries.export.all')}
            </button>
            <button onClick={() => navigate('/journal-entries/add')} style={{ backgroundColor: '#AC2000', border: 'none', color: 'white', padding: '10px 14px', borderRadius: '8px', cursor: 'pointer', display: 'flex', gap: '8px', alignItems: 'center', flexDirection: isRTL ? 'row' : 'row-reverse' }}>
              <FaPlus /> {t('journalEntries.add.title')}
            </button>
          </div>
        </div>
        {/* Filters Section */}
        <div style={{
          backgroundColor: '#1b3350',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '20px',
          direction: dir
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '15px'
          }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold', color: 'white' }}>
              <FaFilter style={{ marginRight: isRTL ? 0 : '8px', marginLeft: isRTL ? '8px' : 0 }} />
              {t('journalEntries.filters.title', { defaultValue: 'الفلترة' })}
            </h3>
            <button
              onClick={() => setShowFilters(!showFilters)}
              style={{
                backgroundColor: '#333',
                color: 'white',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {showFilters ? '−' : '+'}
            </button>
          </div>
          {showFilters && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '15px'
            }}>
              {/* Search */}
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>
                  {t('journalEntries.filters.search', { defaultValue: 'البحث في الوصف أو المرجع' })}
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('journalEntries.filters.searchPlaceholder', { defaultValue: 'بحث...' })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '14px'
                  }}
                />
              </div>
              {/* Status */}
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>
                  {t('journalEntries.filters.status', { defaultValue: 'الحالة' })}
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '14px'
                  }}
                >
                  <option value="">{t('journalEntries.filters.allStatuses', { defaultValue: 'جميع الحالات' })}</option>
                  <option value="draft">{t('journalEntries.status.draft', { defaultValue: 'مسودة' })}</option>
                  <option value="accepted">{t('journalEntries.status.accepted', { defaultValue: 'مقبول' })}</option>
                  <option value="posted">{t('journalEntries.status.posted', { defaultValue: 'مرحل' })}</option>
                  <option value="reversed">{t('journalEntries.status.reversed', { defaultValue: 'معكوس' })}</option>
                </select>
              </div>
              {/* Start Date */}
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>
                  {t('journalEntries.filters.startDate', { defaultValue: 'تاريخ البداية' })}
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '14px'
                  }}
                />
              </div>

              {/* End Date */}
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>
                  {t('journalEntries.filters.endDate', { defaultValue: 'تاريخ النهاية' })}
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '14px'
                  }}
                />
              </div>

              {/* Account Dropdown */}
              <div style={{ position: 'relative' }} className="account-dropdown-container">
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>
                  {t('journalEntries.filters.account', { defaultValue: 'الحساب' })}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={accountId ? accountOptions.find(a => a.id === Number(accountId))?.name_ar || accountOptions.find(a => a.id === Number(accountId))?.name_en || accountOptions.find(a => a.id === Number(accountId))?.name || '' : ''}
                    onClick={() => setAccountDropdownOpen(!accountDropdownOpen)}
                    readOnly
                    placeholder={t('journalEntries.filters.selectAccount', { defaultValue: 'اختر الحساب' })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '6px',
                      color: 'white',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  />
                  {accountDropdownOpen && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: '#1b3350',
                      border: '1px solid #333',
                      borderRadius: '6px',
                      marginTop: '4px',
                      maxHeight: '300px',
                      overflow: 'auto',
                      zIndex: 1000,
                      boxShadow: '0 4px 6px rgba(0,0,0,0.3)'
                    }}>
                      <div style={{ padding: '10px', borderBottom: '1px solid #333' }}>
                        <input
                          ref={accountSearchInputRef}
                          type="text"
                          value={accountSearchTerm}
                          onChange={(e) => setAccountSearchTerm(e.target.value)}
                          placeholder={t('journalEntries.filters.searchAccount', { defaultValue: 'بحث في الحسابات...' })}
                          style={{
                            width: '100%',
                            padding: '8px',
                            backgroundColor: '#1a1f2e',
                            border: '1px solid #333',
                            borderRadius: '4px',
                            color: 'white',
                            fontSize: '14px'
                          }}
                        />
                      </div>
                      <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                        {accountOptions.length === 0 ? (
                          <div style={{ padding: '15px', textAlign: 'center', color: '#9ca3af' }}>
                            {t('journalEntries.filters.noAccounts', { defaultValue: 'لا توجد حسابات' })}
                          </div>
                        ) : (
                          accountOptions.map((account) => (
                            <div
                              key={account.id}
                              onClick={() => {
                                setAccountId(account.id);
                                setSelectedAccountLabel(isRTL ? (account.name_ar || account.name_en || account.name || '') : (account.name_en || account.name_ar || account.name || ''));
                                setAccountDropdownOpen(false);
                                setAccountSearchTerm('');
                              }}
                              style={{
                                padding: '10px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #333',
                                backgroundColor: accountId === account.id ? '#AC2000' : 'transparent',
                                color: accountId === account.id ? 'white' : 'white'
                              }}
                              onMouseEnter={(e) => {
                                if (accountId !== account.id) {
                                  e.target.style.backgroundColor = '#2a3441';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (accountId !== account.id) {
                                  e.target.style.backgroundColor = 'transparent';
                                }
                              }}
                            >
                              {isRTL ? (account.name_ar || account.name) : (account.name_en || account.name)} {account.code ? `(${account.code})` : ''}
                            </div>
                          ))
                        )}
                      </div>
                      {accountPagination && accountPagination.last_page > 1 && (
                        <div style={{
                          padding: '10px',
                          borderTop: '1px solid #333',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <button
                            onClick={() => {
                              if (accountPage > 1) {
                                setAccountPage(accountPage - 1);
                              }
                            }}
                            disabled={accountPage === 1}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: accountPage === 1 ? '#333' : '#AC2000',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: accountPage === 1 ? 'not-allowed' : 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            {t('common.previous', { defaultValue: 'Previous' })}
                          </button>
                          <span style={{ color: 'white', fontSize: '12px' }}>
                            {t('common.page', { defaultValue: 'Page' })} {accountPage} / {accountPagination.last_page}
                          </span>
                          <button
                            onClick={() => {
                              if (accountPage < accountPagination.last_page) {
                                setAccountPage(accountPage + 1);
                              }
                            }}
                            disabled={accountPage >= accountPagination.last_page}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: accountPage >= accountPagination.last_page ? '#333' : '#AC2000',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: accountPage >= accountPagination.last_page ? 'not-allowed' : 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            {t('common.next', { defaultValue: 'Next' })}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Cost Center Dropdown */}
              <div style={{ position: 'relative' }} className="cost-center-dropdown-container">
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>
                  {t('journalEntries.filters.costCenter', { defaultValue: 'مركز التكلفة' })}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={costCenterId ? costCenterOptions.find(c => c.id === Number(costCenterId))?.name_ar || costCenterOptions.find(c => c.id === Number(costCenterId))?.name_en || '' : ''}
                    onClick={() => setCostCenterDropdownOpen(!costCenterDropdownOpen)}
                    readOnly
                    placeholder={t('journalEntries.filters.selectCostCenter', { defaultValue: 'اختر مركز التكلفة' })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '6px',
                      color: 'white',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  />
                  {costCenterDropdownOpen && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: '#1b3350',
                      border: '1px solid #333',
                      borderRadius: '6px',
                      marginTop: '4px',
                      maxHeight: '300px',
                      overflow: 'auto',
                      zIndex: 1000,
                      boxShadow: '0 4px 6px rgba(0,0,0,0.3)'
                    }}>
                      <div style={{ padding: '10px', borderBottom: '1px solid #333' }}>
                        <input
                          ref={costCenterSearchInputRef}
                          type="text"
                          value={costCenterSearchTerm}
                          onChange={(e) => setCostCenterSearchTerm(e.target.value)}
                          placeholder={t('journalEntries.filters.searchCostCenter', { defaultValue: 'بحث في مراكز التكلفة...' })}
                          style={{
                            width: '100%',
                            padding: '8px',
                            backgroundColor: '#1a1f2e',
                            border: '1px solid #333',
                            borderRadius: '4px',
                            color: 'white',
                            fontSize: '14px'
                          }}
                        />
                      </div>
                      {costCenterPagination && costCenterPagination.last_page > 1 && (
                        <div style={{
                          padding: '10px',
                          borderBottom: '1px solid #333',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (costCenterPage > 1) {
                                setCostCenterPage(costCenterPage - 1);
                              }
                            }}
                            disabled={costCenterPage === 1}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: costCenterPage === 1 ? '#333' : '#AC2000',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: costCenterPage === 1 ? 'not-allowed' : 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            {t('common.previous', { defaultValue: 'Previous' })}
                          </button>
                          <span style={{ color: 'white', fontSize: '12px' }}>
                            {t('common.page', { defaultValue: 'Page' })} {costCenterPage} / {costCenterPagination.last_page}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (costCenterPage < costCenterPagination.last_page) {
                                setCostCenterPage(costCenterPage + 1);
                              }
                            }}
                            disabled={costCenterPage >= costCenterPagination.last_page}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: costCenterPage >= costCenterPagination.last_page ? '#333' : '#AC2000',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: costCenterPage >= costCenterPagination.last_page ? 'not-allowed' : 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            {t('common.next', { defaultValue: 'Next' })}
                          </button>
                        </div>
                      )}
                      <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                        {costCenterOptions.length === 0 ? (
                          <div style={{ padding: '15px', textAlign: 'center', color: '#9ca3af' }}>
                            {t('journalEntries.filters.noCostCenters', { defaultValue: 'لا توجد مراكز تكلفة' })}
                          </div>
                        ) : (
                          costCenterOptions.map((costCenter) => (
                            <div
                              key={costCenter.id}
                              onClick={() => {
                                setCostCenterId(costCenter.id);
                                setSelectedCostCenterLabel(isRTL ? (costCenter.name_ar || costCenter.name_en || costCenter.name || '') : (costCenter.name_en || costCenter.name_ar || costCenter.name || ''));
                                setCostCenterDropdownOpen(false);
                                setCostCenterSearchTerm('');
                              }}
                              style={{
                                padding: '10px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #333',
                                backgroundColor: costCenterId === costCenter.id ? '#AC2000' : 'transparent',
                                color: costCenterId === costCenter.id ? 'white' : 'white'
                              }}
                              onMouseEnter={(e) => {
                                if (costCenterId !== costCenter.id) {
                                  e.target.style.backgroundColor = '#2a3441';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (costCenterId !== costCenter.id) {
                                  e.target.style.backgroundColor = 'transparent';
                                }
                              }}
                            >
                              {isRTL ? (costCenter.name_ar || costCenter.name) : (costCenter.name_en || costCenter.name)} {costCenter.code ? `(${costCenter.code})` : ''}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Min Amount */}
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>
                  {t('journalEntries.filters.minAmount', { defaultValue: 'الحد الأدنى للمبلغ' })}
                </label>
                <input
                  type="number"
                  value={minAmount}
                  onChange={(e) => setMinAmount(e.target.value)}
                  placeholder={t('journalEntries.filters.minAmountPlaceholder', { defaultValue: 'الحد الأدنى' })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '14px'
                  }}
                />
              </div>

              {/* Max Amount */}
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>
                  {t('journalEntries.filters.maxAmount', { defaultValue: 'الحد الأقصى للمبلغ' })}
                </label>
                <input
                  type="number"
                  value={maxAmount}
                  onChange={(e) => setMaxAmount(e.target.value)}
                  placeholder={t('journalEntries.filters.maxAmountPlaceholder', { defaultValue: 'الحد الأقصى' })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
          )}

          {showFilters && (
            <div style={{
              display: 'flex',
              gap: '10px',
              marginTop: '15px',
              justifyContent: 'center'
            }}>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('');
                  setStartDate('');
                  setEndDate('');
                  setAccountId('');
                  setCostCenterId('');
                  setMinAmount('');
                  setMaxAmount('');
                  setAppliedSearchTerm('');
                  setAppliedStatusFilter('');
                  setAppliedStartDate('');
                  setAppliedEndDate('');
                  setAppliedAccountId('');
                  setAppliedCostCenterId('');
                  setAppliedMinAmount('');
                  setAppliedMaxAmount('');
                  setSelectedAccountLabel('');
                  setSelectedCostCenterLabel('');
                  setCurrentPage(1);
                }}
                style={{
                  backgroundColor: '#666',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                {t('journalEntries.filters.reset', { defaultValue: 'إعادة التعيين' })}
              </button>
              <button
                onClick={() => {
                  if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
                    toast.error(t('journalEntries.filters.dateError', { defaultValue: 'تاريخ النهاية يجب أن يكون بعد تاريخ البداية' }), { rtl: isRTL });
                    return;
                  }
                  if (minAmount && maxAmount && parseFloat(minAmount) > parseFloat(maxAmount)) {
                    toast.error(t('journalEntries.filters.amountError', { defaultValue: 'الحد الأقصى يجب أن يكون أكبر من الحد الأدنى' }), { rtl: isRTL });
                    return;
                  }
                  setAppliedSearchTerm(searchTerm);
                  setAppliedStatusFilter(statusFilter);
                  setAppliedStartDate(startDate);
                  setAppliedEndDate(endDate);
                  setAppliedAccountId(accountId);
                  setAppliedCostCenterId(costCenterId);
                  setAppliedMinAmount(minAmount);
                  setAppliedMaxAmount(maxAmount);
                  setCurrentPage(1);
                }}
                style={{
                  backgroundColor: '#AC2000',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                {t('journalEntries.filters.apply', { defaultValue: 'تطبيق الفلاتر' })}
              </button>
            </div>
          )}
        </div>

        <div style={{ backgroundColor: '#1b3350', border: '1px solid #333', borderRadius: '12px', overflow: 'hidden', direction: dir }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, direction: dir }}>
              <thead>
                <tr style={{ backgroundColor: '#AC2000' }}>
                  <th style={{ padding: '18px 16px', color: 'white', borderBottom: '2px solid #333', fontSize: '14px', fontWeight: 'bold', textAlign: 'center', width: '60px', minWidth: '60px', backgroundColor: '#AC2000' }}>#</th>
                  <th onClick={() => handleSort(SORT_FIELDS.DATE)} style={{ padding: '18px 16px', color: 'white', borderBottom: '2px solid #333', fontSize: '14px', fontWeight: 'bold', textAlign: 'center', width: '140px', minWidth: '140px', backgroundColor: '#AC2000', cursor: 'pointer', userSelect: 'none' }}>{t('journalEntries.table.date')}{getSortIcon(SORT_FIELDS.DATE)}</th>
                  <th onClick={() => handleSort(SORT_FIELDS.REFERENCE)} style={{ padding: '18px 16px', color: 'white', borderBottom: '2px solid #333', fontSize: '14px', fontWeight: 'bold', textAlign: 'center', width: '180px', minWidth: '180px', backgroundColor: '#AC2000', cursor: 'pointer', userSelect: 'none' }}>{t('journalEntries.table.reference')}{getSortIcon(SORT_FIELDS.REFERENCE)}</th>
                  <th onClick={() => handleSort(SORT_FIELDS.DESCRIPTION)} style={{ padding: '18px 16px', color: 'white', borderBottom: '2px solid #333', fontSize: '14px', fontWeight: 'bold', textAlign: 'center', width: '250px', minWidth: '250px', backgroundColor: '#AC2000', cursor: 'pointer', userSelect: 'none' }}>{t('journalEntries.table.description')}{getSortIcon(SORT_FIELDS.DESCRIPTION)}</th>
                  <th onClick={() => handleSort(SORT_FIELDS.STATUS)} style={{ padding: '18px 16px', color: 'white', borderBottom: '2px solid #333', fontSize: '14px', fontWeight: 'bold', textAlign: 'center', width: '140px', minWidth: '140px', backgroundColor: '#AC2000', cursor: 'pointer', userSelect: 'none' }}>{t('journalEntries.table.status')}{getSortIcon(SORT_FIELDS.STATUS)}</th>
                  <th onClick={() => handleSort(SORT_FIELDS.AMOUNT)} style={{ padding: '18px 16px', color: 'white', borderBottom: '2px solid #333', fontSize: '14px', fontWeight: 'bold', textAlign: 'center', width: '160px', minWidth: '160px', backgroundColor: '#AC2000', cursor: 'pointer', userSelect: 'none' }}>{t('journalEntries.table.amount')}{getSortIcon(SORT_FIELDS.AMOUNT)}</th>
                  <th onClick={() => handleSort(SORT_FIELDS.CREATED_BY)} style={{ padding: '18px 16px', color: 'white', borderBottom: '2px solid #333', fontSize: '14px', fontWeight: 'bold', textAlign: 'center', width: '160px', minWidth: '160px', backgroundColor: '#AC2000', cursor: 'pointer', userSelect: 'none' }}>{t('journalEntries.table.createdBy')}{getSortIcon(SORT_FIELDS.CREATED_BY)}</th>
                  <th style={{ padding: '18px 16px', color: 'white', borderBottom: '2px solid #333', fontSize: '14px', fontWeight: 'bold', textAlign: 'center', width: '240px', minWidth: '240px', backgroundColor: '#AC2000' }}>{t('journalEntries.table.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedJournalEntries.length === 0 ? (
                  <tr>
                    <td colSpan="9" style={{ padding: '40px', textAlign: 'center', color: '#9ca3af', fontSize: '16px', direction: dir }}>
                      {t('journalEntries.noData')}
                    </td>
                  </tr>
                ) : (
                  paginatedJournalEntries.map((e, idx) => {
                    const isLocked = LOCKED_STATUSES.includes(e.status);
                    const hasEntryableData = e.entryable_type && e.entryable_type !== '' && e.entryable_id && e.entryable_id !== 0;
                    const isEditDisabled = isLocked || hasEntryableData;
                    const isDeleteDisabled = isLocked || e.status !== 'draft' || hasEntryableData;
                    const hasAttachments = Array.isArray(e.attachments) && e.attachments.length > 0;
                    return (
                      <tr
                        key={e.id}
                        style={{
                          borderBottom: '1px solid #333',
                          backgroundColor: idx % 2 === 0 ? '#1b3350' : '#1a1f2e',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(ev) => ev.target.closest('tr').style.backgroundColor = '#2a3441'}
                        onMouseLeave={(ev) => ev.target.closest('tr').style.backgroundColor = idx % 2 === 0 ? '#1b3350' : '#1a1f2e'}
                      >
                        <td style={{ padding: '18px 16px', textAlign: 'center', color: 'white', fontSize: '14px' }}>
                          {startIndex + idx + 1}
                        </td>
                        <td style={{ padding: '18px 16px', textAlign: 'center', color: 'white', fontSize: '14px' }}>
                          {(() => {
                            const dateValue = e.entry_date || e.date;
                            if (!dateValue) return '—';
                            try {
                              const date = new Date(dateValue);
                              return date.toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit'
                              });
                            } catch {
                              return dateValue;
                            }
                          })()}
                        </td>
                        <td style={{ padding: '18px 16px', textAlign: 'center', color: 'white', fontSize: '14px', direction: dir }}>
                          {e.reference || '—'}
                        </td>
                        <td style={{ padding: '18px 16px', textAlign: 'center', color: 'white', fontSize: '14px', direction: dir }}>
                          {e.description || '—'}
                        </td>
                        <td style={{ padding: '18px 16px', textAlign: 'center' }}>
                          <span style={{
                            backgroundColor: e.status === 'posted'
                              ? '#AC2000'
                              : e.status === 'accepted'
                                ? '#007bff'
                                : e.status === 'closed'
                                  ? '#6c757d'
                                  : '#666',
                            color: 'white',
                            padding: '6px 14px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            display: 'inline-block'
                          }}>
                            {t(`journalEntries.status.${e.status || 'draft'}`)}
                          </span>
                        </td>
                        <td style={{ padding: '18px 16px', textAlign: 'center', color: 'white', fontSize: '14px', fontWeight: '500' }}>
                          {(() => {
                            const debit = parseFloat(e.total_debit || 0);
                            const credit = parseFloat(e.total_credit || 0);
                            const value = debit !== 0 ? debit : credit;
                            return convertArabicNumbersToEnglish(value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
                          })()}
                        </td>
                        <td style={{ padding: '18px 16px', textAlign: 'center', color: 'white', fontSize: '14px', direction: dir }}>
                          {e.creator || e.created_by?.name || '—'}
                        </td>
                        <td style={{ padding: '18px 16px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center', flexWrap: 'nowrap' }}>
                            <button
                              title={t('journalEntries.attachments.title', { defaultValue: 'المرفقات' })}
                              onClick={() => handleShowAttachments(e)}
                              disabled={!hasAttachments}
                              style={{
                                backgroundColor: hasAttachments ? '#6c757d' : '#555',
                                color: 'white',
                                border: 'none',
                                padding: '10px 12px',
                                borderRadius: '8px',
                                cursor: hasAttachments ? 'pointer' : 'not-allowed',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s',
                                opacity: hasAttachments ? 1 : 0.6
                              }}
                              onMouseEnter={(ev) => {
                                if (!hasAttachments) return;
                                ev.target.style.backgroundColor = '#5a6268';
                                ev.target.style.transform = 'scale(1.05)';
                              }}
                              onMouseLeave={(ev) => {
                                if (!hasAttachments) return;
                                ev.target.style.backgroundColor = '#6c757d';
                                ev.target.style.transform = 'scale(1)';
                              }}
                            >
                              <FaPaperclip />
                            </button>
                            <button
                              title={t('journalEntries.preview.single')}
                              onClick={() => handlePreviewSingle(e)}
                              style={{
                                backgroundColor: '#0d6efd',
                                color: 'white',
                                border: 'none',
                                padding: '10px 12px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(ev) => { ev.target.style.backgroundColor = '#0b5ed7'; ev.target.style.transform = 'scale(1.05)'; }}
                              onMouseLeave={(ev) => { ev.target.style.backgroundColor = '#0d6efd'; ev.target.style.transform = 'scale(1)'; }}
                            >
                              <FaEye />
                            </button>
                            <button
                              title={t('journalEntries.print.single')}
                              onClick={() => handlePrintSingle(e)}
                              disabled={isGeneratingPdf}
                              style={{
                                backgroundColor: isGeneratingPdf ? '#666' : '#1f5ff1',
                                color: 'white',
                                border: 'none',
                                padding: '10px 12px',
                                borderRadius: '8px',
                                cursor: isGeneratingPdf ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s',
                                opacity: isGeneratingPdf ? 0.6 : 1
                              }}
                              onMouseEnter={(ev) => {
                                if (isGeneratingPdf) return;
                                ev.target.style.backgroundColor = '#0b5ed7';
                                ev.target.style.transform = 'scale(1.05)';
                              }}
                              onMouseLeave={(ev) => {
                                if (isGeneratingPdf) return;
                                ev.target.style.backgroundColor = '#1f5ff1';
                                ev.target.style.transform = 'scale(1)';
                              }}
                            >
                              <FaPrint />
                            </button>
                            <button
                              title={t('journalEntries.export.single')}
                              onClick={() => handleExportSingleToExcel(e)}
                              style={{
                                backgroundColor: '#27ae60',
                                color: 'white',
                                border: 'none',
                                padding: '10px 12px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(ev) => { ev.target.style.backgroundColor = '#229954'; ev.target.style.transform = 'scale(1.05)'; }}
                              onMouseLeave={(ev) => { ev.target.style.backgroundColor = '#27ae60'; ev.target.style.transform = 'scale(1)'; }}
                            >
                              <FaFileExcel />
                            </button>
                            <button
                              title={isEditDisabled ? t('journalEntries.actions.cannotEdit') : t('journalEntries.actions.edit')}
                              onClick={() => { if (!isEditDisabled) navigate(`/journal-entries/edit/${e.id}`); }}
                              style={{
                                backgroundColor: isEditDisabled ? '#555' : '#AC2000',
                                color: 'white',
                                border: 'none',
                                padding: '10px 12px',
                                borderRadius: '8px',
                                cursor: isEditDisabled ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s',
                                opacity: isEditDisabled ? 0.6 : 1
                              }}
                              onMouseEnter={(ev) => {
                                if (isEditDisabled) return;
                                ev.target.style.backgroundColor = '#0bb564';
                                ev.target.style.transform = 'scale(1.05)';
                              }}
                              onMouseLeave={(ev) => {
                                if (isEditDisabled) return;
                                ev.target.style.backgroundColor = '#AC2000';
                                ev.target.style.transform = 'scale(1)';
                              }}
                              disabled={isEditDisabled}
                            >
                              <FaEdit />
                            </button>
                            {e.status === 'draft' && (
                              <button
                                title={t('journalEntries.actions.accept')}
                                onClick={() => handleAccept(e.id)}
                                style={{
                                  backgroundColor: '#0d6efd',
                                  color: 'white',
                                  border: 'none',
                                  padding: '10px 12px',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.2s'
                                }}
                                onMouseEnter={(ev) => { ev.target.style.backgroundColor = '#0b5ed7'; ev.target.style.transform = 'scale(1.05)'; }}
                                onMouseLeave={(ev) => { ev.target.style.backgroundColor = '#0d6efd'; ev.target.style.transform = 'scale(1)'; }}
                              >
                                <FaCheck />
                              </button>
                            )}
                            {e.status === 'accepted' && (
                              <button
                                title={t('journalEntries.actions.post')}
                                onClick={() => handlePost(e.id)}
                                style={{
                                  backgroundColor: '#845ef7',
                                  color: 'white',
                                  border: 'none',
                                  padding: '10px 12px',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.2s'
                                }}
                                onMouseEnter={(ev) => { ev.target.style.backgroundColor = '#7950f2'; ev.target.style.transform = 'scale(1.05)'; }}
                                onMouseLeave={(ev) => { ev.target.style.backgroundColor = '#845ef7'; ev.target.style.transform = 'scale(1)'; }}
                              >
                                <FaPaperPlane />
                              </button>
                            )}
                            {e.status === 'posted' && (
                              <button
                                title={t('journalEntries.actions.reverse')}
                                onClick={() => handleReverse(e.id)}
                                style={{
                                  backgroundColor: '#ff6b6b',
                                  color: 'white',
                                  border: 'none',
                                  padding: '10px 12px',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.2s'
                                }}
                                onMouseEnter={(ev) => { ev.target.style.backgroundColor = '#ee5a52'; ev.target.style.transform = 'scale(1.05)'; }}
                                onMouseLeave={(ev) => { ev.target.style.backgroundColor = '#ff6b6b'; ev.target.style.transform = 'scale(1)'; }}
                              >
                                <FaUndo />
                              </button>
                            )}
                            {(e.status === 'draft' || e.status === 'closed') && (
                              <button
                                title={isDeleteDisabled ? t('journalEntries.actions.cannotDelete') : t('journalEntries.actions.delete')}
                                onClick={() => {
                                  if (!isDeleteDisabled) {
                                    handleDelete(e.id);
                                  }
                                }}
                                style={{
                                  backgroundColor: isDeleteDisabled ? '#555' : '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  padding: '10px 12px',
                                  borderRadius: '8px',
                                  cursor: isDeleteDisabled ? 'not-allowed' : 'pointer',
                                  fontSize: '14px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.2s',
                                  opacity: isDeleteDisabled ? 0.6 : 1
                                }}
                                onMouseEnter={(ev) => {
                                  if (isDeleteDisabled) return;
                                  ev.target.style.backgroundColor = '#bb2d3b';
                                  ev.target.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(ev) => {
                                  if (isDeleteDisabled) return;
                                  ev.target.style.backgroundColor = '#dc3545';
                                  ev.target.style.transform = 'scale(1)';
                                }}
                                disabled={isDeleteDisabled}
                              >
                                <FaTrash />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {totalPages > 0 && (
            <div
              style={{
                padding: '20px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '10px',
                borderTop: '1px solid #333',
                flexDirection: isRTL ? 'row-reverse' : 'row',
                direction: isRTL ? 'rtl' : 'ltr'
              }}
            >
              {isRTL ? (
                <>
                  {/* Next Button (RTL: leftmost) */}
                  {currentPageSafe < totalPages && (
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      style={{
                        backgroundColor: '#1b3350',
                        color: 'white',
                        border: '1px solid #333',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                      title={t('common.lastPage')}
                    >
                      {'>>'}
                    </button>
                  )}
                  {/* Page Numbers */}
                  {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                    let page;
                    if (totalPages <= 10) {
                      page = i + 1;
                    } else if (currentPageSafe <= 5) {
                      page = i + 1;
                    } else if (currentPageSafe >= totalPages - 4) {
                      page = totalPages - 9 + i;
                    } else {
                      page = currentPageSafe - 4 + i;
                    }
                    return page;
                  })
                    .reverse()
                    .map(page => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        style={{
                          backgroundColor: page === currentPageSafe ? '#AC2000' : '#1b3350',
                          color: 'white',
                          border: page === currentPageSafe ? 'none' : '1px solid #333',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: page === currentPageSafe ? 'bold' : 'normal'
                        }}
                      >
                        {page}
                      </button>
                    ))}
                  {/* Previous Button (RTL: rightmost) */}
                  {currentPageSafe > 1 && (
                    <button
                      onClick={() => setCurrentPage(1)}
                      style={{
                        backgroundColor: '#1b3350',
                        color: 'white',
                        border: '1px solid #333',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                      title={t('common.firstPage')}
                    >
                      {'<<'}
                    </button>
                  )}
                  {/* Page Info */}
                  <span style={{ color: '#9ca3af', fontSize: '14px', marginRight: '10px' }}>
                    {t('common.pageInfo', {
                      current: currentPageSafe,
                      total: totalPages
                    })}
                  </span>
                </>
              ) : (
                <>
                  {/* Previous Button (LTR: leftmost) */}
                  {currentPageSafe > 1 && (
                    <button
                      onClick={() => setCurrentPage(1)}
                      style={{
                        backgroundColor: '#1b3350',
                        color: 'white',
                        border: '1px solid #333',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                      title={t('common.firstPage')}
                    >
                      {'<<'}
                    </button>
                  )}
                  {/* Page Numbers */}
                  {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                    let page;
                    if (totalPages <= 10) {
                      page = i + 1;
                    } else if (currentPageSafe <= 5) {
                      page = i + 1;
                    } else if (currentPageSafe >= totalPages - 4) {
                      page = totalPages - 9 + i;
                    } else {
                      page = currentPageSafe - 4 + i;
                    }
                    return page;
                  }).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      style={{
                        backgroundColor: page === currentPageSafe ? '#AC2000' : '#1b3350',
                        color: 'white',
                        border: page === currentPageSafe ? 'none' : '1px solid #333',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: page === currentPageSafe ? 'bold' : 'normal'
                      }}
                    >
                      {page}
                    </button>
                  ))}
                  {/* Next Button (LTR: rightmost) */}
                  {currentPageSafe < totalPages && (
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      style={{
                        backgroundColor: '#1b3350',
                        color: 'white',
                        border: '1px solid #333',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                      title={t('common.lastPage')}
                    >
                      {'>>'}
                    </button>
                  )}
                  {/* Page Info */}
                  <span style={{ color: '#9ca3af', fontSize: '14px', marginLeft: '10px' }}>
                    {t('common.pageInfo', {
                      current: currentPageSafe,
                      total: totalPages
                    })}
                  </span>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999999 }}>
          <div style={{ backgroundColor: '#1f2937', border: '1px solid #333', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '420px', direction: dir }}>
            <h3 style={{ color: 'white', marginBottom: '12px', fontSize: '18px', textAlign }}>{t('journalEntries.delete.title')}</h3>
            <p style={{ color: '#ccc', marginBottom: '8px', fontSize: '14px', textAlign }}>{t('journalEntries.delete.confirm')}</p>
            {entryToDelete && (
              <div style={{ color: 'white', fontSize: '14px', marginBottom: '16px', textAlign, direction: dir }}>
                {t('journalEntries.table.reference')}: {entryToDelete.reference || '—'}
              </div>
            )}
            <p style={{ color: '#ef4444', marginBottom: '20px', fontSize: '12px', textAlign }}>{t('journalEntries.delete.irreversible')}</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <button onClick={cancelDelete} style={{ backgroundColor: '#666', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 16px', cursor: 'pointer' }}>{t('journalEntries.delete.cancel')}</button>
              <button onClick={confirmDelete} style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 16px', cursor: 'pointer' }}>{t('journalEntries.delete.confirmButton')}</button>
            </div>
          </div>
        </div>
      )}

      {attachmentsModal.open && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            zIndex: 999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16
          }}
          onClick={() => setAttachmentsModal({ open: false, attachments: [], entryId: null })}
        >
          <div
            style={{
              backgroundColor: '#1f2937',
              border: '1px solid #333',
              borderRadius: 12,
              padding: 24,
              width: '90%',
              maxWidth: 900,
              maxHeight: '90vh',
              overflow: 'auto',
              margin: 'auto',
              direction: dir,
              textAlign
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: 16, color: 'white' }}>
              {t('journalEntries.attachmentsModal.title', { defaultValue: 'المرفقات' })}
            </h3>

            {attachmentsModal.attachments && Array.isArray(attachmentsModal.attachments) && attachmentsModal.attachments.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
                {attachmentsModal.attachments.map((attachment, idx) => {
                  const isImage = attachment.mime_type?.startsWith('image/');
                  const fileUrl =
                    attachment?.url ||
                    attachment?.file_url ||
                    attachment?.media_url ||
                    (attachment?.path ? `${BASE_URL}/storage/${attachment.path}` : '') ||
                    attachment?.full_url ||
                    attachment?.link ||
                    '';

                  return (
                    <div
                      key={attachment.id || idx}
                      style={{
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: 8,
                        padding: 12,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8
                      }}
                    >
                      {isImage && fileUrl ? (
                        <div style={{ position: 'relative', width: '100%', paddingTop: '75%', backgroundColor: '#14263b', borderRadius: 4, overflow: 'hidden' }}>
                          <img
                            src={fileUrl}
                            alt={attachment.file_name || `Attachment ${idx + 1}`}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                              cursor: 'pointer'
                            }}
                            onClick={() => window.open(fileUrl, '_blank')}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const errorDiv = e.target.nextSibling;
                              if (errorDiv) errorDiv.style.display = 'flex';
                            }}
                          />
                          <div style={{ display: 'none', position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', color: '#888', fontSize: 12 }}>
                            {t('journalEntries.attachmentsModal.imageError', { defaultValue: 'خطأ في تحميل الصورة' })}
                          </div>
                        </div>
                      ) : (
                        <div style={{ width: '100%', paddingTop: '75%', backgroundColor: '#14263b', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>
                          <FaFileAlt style={{ fontSize: 32 }} />
                        </div>
                      )}
                      <div style={{ fontSize: 12, color: '#ccc', wordBreak: 'break-word', textAlign: 'center' }}>
                        {attachment.file_name || attachment.name || `Attachment ${idx + 1}`}
                      </div>
                      {fileUrl && (
                        <a
                          href={fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            backgroundColor: '#AC2000',
                            color: 'white',
                            border: 'none',
                            padding: '8px 12px',
                            borderRadius: 6,
                            cursor: 'pointer',
                            textAlign: 'center',
                            textDecoration: 'none',
                            fontSize: 12,
                            fontWeight: 'bold'
                          }}
                        >
                          {isImage
                            ? t('journalEntries.attachmentsModal.view', { defaultValue: 'عرض' })
                            : t('journalEntries.attachmentsModal.download', { defaultValue: 'تحميل' })}
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: 40, color: '#ccc' }}>
                {t('journalEntries.attachmentsModal.noAttachments', { defaultValue: 'لا توجد مرفقات' })}
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 24 }}>
              <button
                onClick={() => setAttachmentsModal({ open: false, attachments: [], entryId: null })}
                style={{
                  backgroundColor: '#666',
                  color: 'white',
                  border: 'none',
                  padding: '10px 16px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 'bold'
                }}
              >
                {t('journalEntries.attachmentsModal.close', { defaultValue: 'إغلاق' })}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Dynamic measurement template for page splitting */}
      <JournalEntriesPdfMeasurementTemplate
        journalEntries={sortEntries(allJournalEntriesForPDF || [], sortField, sortOrder)}
        isRTL={isRTL}
        t={t}
        activeFiltersText={activeFiltersForPdf}
        headerLogo={headerLogo}
        publicUrl={publicUrl}
      />

      {/* Print Templates for All Journal Entries — rendered ONLY during PDF
          generation. Rendering them alongside the preview modal doubled the
          DOM (2x A4 pages) and froze the browser with large datasets. */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
        {isGeneratingPdf && pdfChunks.map((chunk, chunkIndex) => (
          <JournalEntriesPdfPageTemplate
            key={chunkIndex}
            id={`journal-entries-pdf-all-${chunkIndex}`}
            chunk={chunk}
            chunkIndex={chunkIndex}
            totalChunks={pdfChunks.length}
            isRTL={isRTL}
            t={t}
            publicUrl={publicUrl}
            headerLogo={headerLogo}
            activeFiltersText={activeFiltersForPdf}
          />
        ))}
      </div>

      {/* Preview Modal */}
      <PdfPreviewModal
        isOpen={isPreviewOpen && pdfChunks && pdfChunks.length > 0}
        title={t('journalEntries.title')}
        onClose={() => {
          setIsPreviewOpen(false);
          setPdfChunks([]);
          setAllJournalEntriesForPDF([]);
        }}
        onPrint={handlePrintAll}
        isGeneratingPdf={isGeneratingPdf}
      >
        {(pdfChunks || []).map((chunk, chunkIndex) => (
          <JournalEntriesPdfPageTemplate
            key={chunkIndex}
            chunk={chunk}
            chunkIndex={chunkIndex}
            totalChunks={pdfChunks.length}
            isRTL={isRTL}
            t={t}
            publicUrl={publicUrl}
            headerLogo={headerLogo}
            activeFiltersText={activeFiltersForPdf}
            style={{ marginBottom: chunkIndex < (pdfChunks || []).length - 1 ? '20px' : 0 }}
          />
        ))}
      </PdfPreviewModal>

      {/* Print Template for Single Journal Entry */}
      {singleEntryForPreview && (
        <div
          id={`journal-entry-pdf-single-${singleEntryForPreview.id}`}
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '-9999px',
            top: 0,
            width: '794px',
            minHeight: '1123px',
            height: 'auto',
            backgroundColor: '#ffffff',
            color: '#000000',
            fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: 'bold',
            padding: '40px',
            boxSizing: 'border-box',
            overflow: 'visible'
          }}
        >
          <div style={{
            border: '5px solid #000',
            padding: '3px',
            backgroundColor: '#ffffff',
            boxSizing: 'border-box',
            width: '100%',
            minHeight: '1043px',
            height: 'auto',
            overflow: 'visible'
          }}>
            <div style={{
              border: '2px solid #000',
              minHeight: '1033px',
              height: '1033px',
              padding: '18px 18px 10px',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: 'bold',
              direction: isRTL ? 'rtl' : 'ltr',
              backgroundColor: '#ffffff',
              boxSizing: 'border-box',
              width: '100%',
              overflow: 'visible'
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundImage: `url(${headerLogo})`,
                backgroundSize: '95%',
                backgroundPosition: 'center center',
                backgroundRepeat: 'no-repeat',
                opacity: 0.08,
                zIndex: 0,
                pointerEvents: 'none'
              }} />
              <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
                <img
                  src={`${publicUrl}/HeaderAce.png`}
                  alt="Header"
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'contain',
                    marginBottom: '8px',
                    borderBottom: '4px double #000',
                    paddingBottom: '8px',
                    flexShrink: 0
                  }}
                  crossOrigin="anonymous"
                />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
                  <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '10px' }}>
                    <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0, direction: dir }}>
                      {t('journalEntries.title')}
                    </h2>
                    <p style={{ fontSize: '11px', color: '#555', margin: '6px 0 0 0' }}>
                      {new Date().toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  </div>
                  <div style={{ direction: isRTL ? 'rtl' : 'ltr', fontFamily: '"Times New Roman", "Times", serif' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                      <thead>
                        <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
                          <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('journalEntries.table.serialNumber')}</th>
                          <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('journalEntries.table.date')}</th>
                          <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('journalEntries.table.reference')}</th>
                          <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('journalEntries.table.description')}</th>
                          <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('journalEntries.table.status')}</th>
                          <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('journalEntries.table.amount')}</th>
                          <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('journalEntries.table.createdBy')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>1</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{(() => {
                            const dateValue = singleEntryForPreview.entry_date || singleEntryForPreview.date;
                            if (!dateValue) return '—';
                            try {
                              const date = new Date(dateValue);
                              return date.toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit'
                              });
                            } catch {
                              return dateValue;
                            }
                          })()}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{singleEntryForPreview.reference || '—'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{singleEntryForPreview.description || '—'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{t(`journalEntries.status.${singleEntryForPreview.status || 'draft'}`)}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{(() => {
                            const debit = parseFloat(singleEntryForPreview.total_debit || 0);
                            const credit = parseFloat(singleEntryForPreview.total_credit || 0);
                            const value = debit !== 0 ? debit : credit;
                            return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                          })()}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{singleEntryForPreview.creator || singleEntryForPreview.created_by?.name || '—'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div style={{
                position: 'absolute',
                bottom: '10px',
                left: '18px',
                right: '18px',
                flexShrink: 0
              }}>
                <img
                  src={`${publicUrl}/FooterAce.png`}
                  alt="Footer"
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'contain',
                    borderTop: '4px double #000',
                    paddingTop: '8px',
                    flexShrink: 0
                  }}
                  crossOrigin="anonymous"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal for Single Entry */}
      <PdfPreviewModal
        isOpen={isSinglePreviewOpen && singleEntryForPreview}
        title={t('journalEntries.title')}
        onClose={() => {
          setIsSinglePreviewOpen(false);
          setSingleEntryForPreview(null);
        }}
        onPrint={() => singleEntryForPreview && handlePrintSingle(singleEntryForPreview)}
        isGeneratingPdf={isGeneratingPdf}
      >
        {singleEntryForPreview && (
          <div
            id={`preview-journal-entry-pdf-single-${singleEntryForPreview.id}`}
            style={{
              width: '794px',
              minHeight: '1123px',
              height: 'auto',
              backgroundColor: '#ffffff',
              color: '#000000',
              fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: 'bold',
              padding: '40px',
              boxSizing: 'border-box'
            }}
          >
            <div style={{
              border: '5px solid #000',
              padding: '3px',
              backgroundColor: '#ffffff',
              boxSizing: 'border-box',
              width: '100%',
              minHeight: '1043px',
              height: 'auto',
              overflow: 'visible'
            }}>
              <div style={{
                border: '2px solid #000',
                minHeight: '1033px',
                height: '1033px',
                padding: '18px 18px 10px',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: 'bold',
                direction: isRTL ? 'rtl' : 'ltr',
                backgroundColor: '#ffffff',
                boxSizing: 'border-box',
                width: '100%',
                overflow: 'visible'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundImage: `url(${headerLogo})`,
                  backgroundSize: '95%',
                  backgroundPosition: 'center center',
                  backgroundRepeat: 'no-repeat',
                  opacity: 0.08,
                  zIndex: 0,
                  pointerEvents: 'none'
                }} />
                <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
                  <img
                    src={`${publicUrl}/HeaderAce.png`}
                    alt="Header"
                    style={{
                      width: '100%',
                      height: 'auto',
                      objectFit: 'contain',
                      marginBottom: '8px',
                      borderBottom: '4px double #000',
                      paddingBottom: '8px',
                      flexShrink: 0
                    }}
                    crossOrigin="anonymous"
                  />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
                    <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '10px' }}>
                      <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0, direction: dir }}>
                        {t('journalEntries.title')}
                      </h2>
                      <p style={{ fontSize: '11px', color: '#555', margin: '6px 0 0 0' }}>
                        {new Date().toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </p>
                    </div>
                    {(appliedSearchTerm || appliedStatusFilter || appliedStartDate || appliedEndDate || appliedAccountId || appliedCostCenterId || appliedMinAmount || appliedMaxAmount) && (
                      <div style={{ fontSize: '10px', color: '#333', marginBottom: '8px', padding: '4px 8px', backgroundColor: 'rgba(238, 242, 247, 0.8)', border: '1px solid #ccc', direction: dir }}>
                        {[
                          appliedSearchTerm && `${t('journalEntries.filters.search')}: ${appliedSearchTerm}`,
                          appliedStatusFilter && `${t('journalEntries.filters.status')}: ${t(`journalEntries.status.${appliedStatusFilter}`)}`,
                          appliedStartDate && `${t('journalEntries.filters.startDate')}: ${appliedStartDate}`,
                          appliedEndDate && `${t('journalEntries.filters.endDate')}: ${appliedEndDate}`,
                          appliedAccountId && `${t('journalEntries.filters.account')}: ${selectedAccountLabel || appliedAccountId}`,
                          appliedCostCenterId && `${t('journalEntries.filters.costCenter')}: ${selectedCostCenterLabel || appliedCostCenterId}`,
                          appliedMinAmount && `${t('journalEntries.filters.minAmount')}: ${appliedMinAmount}`,
                          appliedMaxAmount && `${t('journalEntries.filters.maxAmount')}: ${appliedMaxAmount}`,
                        ].filter(Boolean).join(' | ')}
                      </div>
                    )}
                    <div style={{ direction: isRTL ? 'rtl' : 'ltr', fontFamily: '"Times New Roman", "Times", serif' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                        <thead>
                          <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('journalEntries.table.serialNumber')}</th>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('journalEntries.table.date')}</th>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('journalEntries.table.reference')}</th>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('journalEntries.table.description')}</th>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('journalEntries.table.status')}</th>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('journalEntries.table.amount')}</th>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('journalEntries.table.createdBy')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                            <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>1</td>
                            <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{(() => {
                              const dateValue = singleEntryForPreview.entry_date || singleEntryForPreview.date;
                              if (!dateValue) return '—';
                              try {
                                const date = new Date(dateValue);
                                return date.toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit'
                                });
                              } catch {
                                return dateValue;
                              }
                            })()}</td>
                            <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{singleEntryForPreview.reference || '—'}</td>
                            <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{singleEntryForPreview.description || '—'}</td>
                            <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{t(`journalEntries.status.${singleEntryForPreview.status || 'draft'}`)}</td>
                            <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{(() => {
                              const debit = parseFloat(singleEntryForPreview.total_debit || 0);
                              const credit = parseFloat(singleEntryForPreview.total_credit || 0);
                              const value = debit !== 0 ? debit : credit;
                              return convertArabicNumbersToEnglish(value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
                            })()}</td>
                            <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{singleEntryForPreview.creator || singleEntryForPreview.created_by?.name || '—'}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <div style={{
                  position: 'absolute',
                  bottom: '10px',
                  left: '18px',
                  right: '18px',
                  flexShrink: 0
                }}>
                  <img
                    src={`${publicUrl}/FooterAce.png`}
                    alt="Footer"
                    style={{
                      width: '100%',
                      height: 'auto',
                      objectFit: 'contain',
                      borderTop: '4px double #000',
                      paddingTop: '8px',
                      flexShrink: 0
                    }}
                    crossOrigin="anonymous"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </PdfPreviewModal>
    </React.Fragment>
  );
};

export default JournalEntries;

// Unified PDF Page Template component for preview and print
const JournalEntriesPdfPageTemplate = ({
  chunk,
  chunkIndex,
  totalChunks,
  isRTL,
  t,
  publicUrl,
  headerLogo,
  activeFiltersText,
  id,
  style = {}
}) => {
  const dir = isRTL ? 'rtl' : 'ltr';

  return (
    <div
      id={id}
      style={{
        width: '794px',
        minHeight: '1123px',
        height: 'auto',
        backgroundColor: '#ffffff',
        color: '#000000',
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: 'bold',
        padding: '40px',
        boxSizing: 'border-box',
        overflow: 'visible',
        position: 'relative',
        ...style
      }}
    >
      <div style={{
        border: '5px solid #000',
        padding: '3px',
        backgroundColor: '#ffffff',
        boxSizing: 'border-box',
        width: '100%',
        minHeight: '1043px',
        height: 'auto',
        overflow: 'visible'
      }}>
        <div style={{
          border: '2px solid #000',
          minHeight: '1033px',
          height: '1033px',
          padding: '18px 18px 10px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
          fontWeight: 'bold',
          direction: isRTL ? 'rtl' : 'ltr',
          backgroundColor: '#ffffff',
          boxSizing: 'border-box',
          width: '100%',
          overflow: 'visible'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url(${headerLogo})`,
            backgroundSize: '95%',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.08,
            zIndex: 0,
            pointerEvents: 'none'
          }} />
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
            <img
              src={`${publicUrl}/HeaderAce.png`}
              alt="Header"
              style={{
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                marginBottom: '8px',
                borderBottom: '4px double #000',
                paddingBottom: '8px',
                flexShrink: 0
              }}
              crossOrigin="anonymous"
            />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
              <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '10px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0 }}>
                  {t('journalEntries.title')}
                </h2>
                <p style={{ fontSize: '11px', color: '#555', margin: '6px 0 0 0' }}>
                  {new Date().toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>
              {activeFiltersText && (
                <div style={{ fontSize: '10px', color: '#333', marginBottom: '8px', padding: '4px 8px', backgroundColor: 'rgba(238, 242, 247, 0.8)', border: '1px solid #ccc', direction: dir }}>
                  {activeFiltersText}
                </div>
              )}
              <div style={{ direction: isRTL ? 'rtl' : 'ltr', fontFamily: '"Times New Roman", "Times", serif' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif', backgroundColor: 'rgba(255, 255, 255, 0.7)', tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: '5%' }} />
                    <col style={{ width: '11%' }} />
                    <col style={{ width: '13%' }} />
                    <col style={{ width: '38%' }} />
                    <col style={{ width: '9%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '12%' }} />
                  </colgroup>
                  <thead>
                    <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', verticalAlign: 'middle' }}>{t('journalEntries.table.serialNumber')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', verticalAlign: 'middle' }}>{t('journalEntries.table.date')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', verticalAlign: 'middle' }}>{t('journalEntries.table.reference')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', verticalAlign: 'middle' }}>{t('journalEntries.table.description')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', verticalAlign: 'middle' }}>{t('journalEntries.table.status')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', verticalAlign: 'middle' }}>{t('journalEntries.table.amount')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', verticalAlign: 'middle' }}>{t('journalEntries.table.createdBy')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chunk.items.map((entry, idx) => {
                      const globalIndex = chunk.startIndex + idx;
                      return (
                        <tr key={entry.id || idx} style={{ backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', fontSize: '11px', wordBreak: 'break-word' }}>{globalIndex + 1}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', fontSize: '11px', wordBreak: 'break-word' }}>
                            {(() => {
                              const dateValue = entry.entry_date || entry.date;
                              if (!dateValue) return '—';
                              try {
                                const date = new Date(dateValue);
                                return date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
                              } catch { return dateValue; }
                            })()}
                          </td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', fontSize: '11px', wordBreak: 'break-word' }}>{entry.reference || '—'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', verticalAlign: 'middle', fontSize: '11px', wordBreak: 'break-word' }}>{entry.description || '—'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', fontSize: '11px', wordBreak: 'break-word' }}>{t(`journalEntries.status.${entry.status || 'draft'}`)}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', fontSize: '11px', wordBreak: 'break-word' }}>
                            {(() => {
                              const debit = parseFloat(entry.total_debit || 0);
                              const credit = parseFloat(entry.total_credit || 0);
                              const value = debit !== 0 ? debit : credit;
                              return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                            })()}
                          </td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', fontSize: '10px', wordBreak: 'break-word' }}>{entry.creator || entry.created_by?.name || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Page number footer */}
            <div style={{
              position: 'absolute',
              bottom: '95px',
              left: '18px',
              right: '18px',
              display: 'flex',
              justifyContent: 'center',
              fontSize: '11px',
              color: '#555',
              fontFamily: '"Times New Roman", "Times", serif'
            }}>
              {isRTL ? `صفحة ${chunkIndex + 1} من ${totalChunks}` : `Page ${chunkIndex + 1} of ${totalChunks}`}
            </div>

            <div style={{
              position: 'absolute',
              bottom: '10px',
              left: '18px',
              right: '18px',
              flexShrink: 0
            }}>
              <img
                src={`${publicUrl}/FooterAce.png`}
                alt="Footer"
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  borderTop: '4px double #000',
                  paddingTop: '8px',
                  flexShrink: 0
                }}
                crossOrigin="anonymous"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Offscreen measurement template component to compute actual row heights
const JournalEntriesPdfMeasurementTemplate = ({
  journalEntries,
  isRTL,
  t,
  activeFiltersText = '',
  headerLogo,
  publicUrl
}) => {
  const dir = isRTL ? 'rtl' : 'ltr';

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        left: '-9999px',
        top: 0,
        width: '794px',
        minHeight: '1123px',
        backgroundColor: '#ffffff',
        color: '#000000',
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: 'bold',
        padding: '40px',
        boxSizing: 'border-box',
        overflow: 'visible'
      }}
    >
      <div style={{ border: '5px solid #000', padding: '3px', backgroundColor: '#ffffff', boxSizing: 'border-box', width: '100%', minHeight: '1043px' }}>
        <div style={{ border: '2px solid #000', minHeight: '1033px', height: '1033px', padding: '18px 18px 10px', position: 'relative', display: 'flex', flexDirection: 'column', direction: isRTL ? 'rtl' : 'ltr', backgroundColor: '#ffffff' }}>
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
            <div id="journal-entries-pdf-measure-top-header" style={{ width: '100%', marginBottom: '12px' }}>
              <img
                src={`${publicUrl}/HeaderAce.png`}
                alt=""
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  marginBottom: '8px',
                  borderBottom: '4px double #000',
                  paddingBottom: '8px',
                  flexShrink: 0
                }}
                crossOrigin="anonymous"
              />
              <div style={{ marginTop: '12px', textAlign: 'center' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0 }}>
                  {t('journalEntries.title')}
                </h2>
                <p style={{ fontSize: '11px', color: '#555', margin: '6px 0 0 0' }}>
                  {new Date().toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
                {activeFiltersText && (
                  <div style={{ fontSize: '10px', color: '#333', marginTop: '8px', padding: '4px 8px', backgroundColor: 'rgba(238, 242, 247, 0.8)', border: '1px solid #ccc', direction: dir }}>
                    {activeFiltersText}
                  </div>
                )}
              </div>
            </div>
            <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif' }}>
              <colgroup>
                <col style={{ width: '5%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '38%' }} />
                <col style={{ width: '9%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '12%' }} />
              </colgroup>
              <thead id="journal-entries-pdf-measure-thead">
                <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '35px' }}>{t('journalEntries.table.serialNumber')}</th>
                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '75px' }}>{t('journalEntries.table.date')}</th>
                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '100px' }}>{t('journalEntries.table.reference')}</th>
                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '224px' }}>{t('journalEntries.table.description')}</th>
                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '80px' }}>{t('journalEntries.table.status')}</th>
                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '90px' }}>{t('journalEntries.table.amount')}</th>
                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '110px' }}>{t('journalEntries.table.createdBy')}</th>
                </tr>
              </thead>
              <tbody id="journal-entries-pdf-measure-tbody">
                {journalEntries.map((entry, idx) => (
                  <tr key={entry.id || idx}>
                    <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{idx + 1}</td>
                    <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>
                      {(() => {
                        const dateValue = entry.entry_date || entry.date;
                        if (!dateValue) return '—';
                        try {
                          const date = new Date(dateValue);
                          return date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
                        } catch { return dateValue; }
                      })()}
                    </td>
                    <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{entry.reference || '—'}</td>
                    <td style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', verticalAlign: 'middle', wordBreak: 'break-word' }}>{entry.description || '—'}</td>
                    <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{t(`journalEntries.status.${entry.status || 'draft'}`)}</td>
                    <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>
                      {(() => {
                        const debit = parseFloat(entry.total_debit || 0);
                        const credit = parseFloat(entry.total_credit || 0);
                        const value = debit !== 0 ? debit : credit;
                        return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                      })()}
                    </td>
                    <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{entry.creator || entry.created_by?.name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Fixed footer reserve — mirrors the print template's
                paddingBottom (120px footer + page-number line). Never use an
                <img> here: its measured height is 0 until it loads, which
                under-reserves space and lets rows collide with the footer. */}
            <div id="journal-entries-pdf-measure-footer" style={{ height: '135px', flexShrink: 0, width: '100%' }} />
          </div>
        </div>
      </div>
    </div>
  );
};


